import React, { useEffect, useMemo, useState } from 'react';

export default function AdminCustomerPortal() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        // Derive customers from appointments (group by email)
        const res = await fetch('/api/get_appointments.php', { credentials: 'include' });
        const data = await res.json();
        const appts = Array.isArray(data?.appointments) ? data.appointments : [];
        const byEmail = new Map();
        for (const a of appts) {
          const key = a.email || 'unknown';
          const cur = byEmail.get(key) || { email: key, name: a.name, phone: a.phone, count: 0, last: a.date };
          cur.count += 1;
          cur.last = cur.last && a.date && cur.last > a.date ? cur.last : a.date;
          byEmail.set(key, cur);
        }
        if (!cancelled) setRows(Array.from(byEmail.values()));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => !q || [r.name, r.email, r.phone].some((v) => String(v || '').toLowerCase().includes(q)));
  }, [rows, query]);

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Admin Customer Portal</h1>
        <input className="h-9 px-3 border rounded-md" placeholder="Search name/email/phone" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Search customers" />
      </div>

      <div className="rounded-lg border overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Phone</Th>
              <Th>Appointments</Th>
              <Th>Last Visit</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td className="p-3" colSpan={6}>Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td className="p-3" colSpan={6}>No customers found.</td></tr>
            ) : (
              filtered.map((r) => (
                <tr key={r.email} className="border-t hover:bg-gray-50">
                  <Td>{r.name || '—'}</Td>
                  <Td>{r.email}</Td>
                  <Td>{r.phone || '—'}</Td>
                  <Td>{r.count}</Td>
                  <Td>{r.last || '—'}</Td>
                  <Td className="text-right">
                    <button className="text-blue-600 hover:underline mr-3" aria-label={`View details for ${r.email}`}>View</button>
                    <button className="text-gray-600 hover:underline mr-3" aria-label={`Export records for ${r.email}`}>Export</button>
                    <button className="text-red-600 hover:underline" aria-label={`Delete latest appointment for ${r.email}`}>Delete</button>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Th({ children, className = '' }) {
  return <th className={`p-3 font-medium text-left ${className}`}>{children}</th>;
}

function Td({ children, className = '' }) {
  return <td className={`p-3 align-top ${className}`}>{children}</td>;
}
